import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Search, MapPin, Megaphone, BarChart3, Brain, Sparkles, Stars,
  Check, X, Loader2, AlertCircle, Trash2, Zap, Eye, EyeOff,
  ExternalLink, Shield, ChevronRight, Mail, Database, Send,
} from 'lucide-react'
import api from '@/shared/lib/api'
import { useAuthStore } from '@/core/stores/auth'
import { cn } from '@/shared/lib/utils'

// ── Provider config ─────────────────────────────────────

const LUCIDE_ICONS: Record<string, React.ElementType> = {
  search: Search,
  'map-pin': MapPin,
  megaphone: Megaphone,
  'bar-chart': BarChart3,
  brain: Brain,
  sparkles: Sparkles,
  stars: Stars,
  mail: Mail,
  database: Database,
  send: Send,
}

// SVG brand icons for CRM and Email providers
function BrandIcon({ provider, size = 18 }: { provider: string; size?: number }) {
  const s = size
  const icons: Record<string, JSX.Element> = {
    hubspot: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.4 8.2V5.6c.7-.3 1.2-1 1.2-1.8 0-1.1-.9-2-2-2s-2 .9-2 2c0 .8.5 1.5 1.2 1.8v2.6c-1.2.3-2.3.9-3.1 1.8L5.5 5.2c.1-.3.2-.5.2-.8 0-1.2-1-2.2-2.2-2.2S1.3 3.2 1.3 4.4s1 2.2 2.2 2.2c.4 0 .8-.1 1.1-.3l7 4.7c-.5.9-.8 1.9-.8 3 0 3.3 2.7 6 6 6s6-2.7 6-6c0-2.8-1.9-5.1-4.4-5.8zM16.8 18c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/>
      </svg>
    ),
    pipedrive: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
        <circle cx="12" cy="12" r="4" fill="currentColor"/>
      </svg>
    ),
    salesforce: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 4.5c1.2-1.3 2.9-2 4.7-2 2.8 0 5.2 1.7 6.2 4.2 2.3.5 4.1 2.6 4.1 5.1 0 2.9-2.3 5.2-5.2 5.2H5.5C2.5 17 0 14.5 0 11.5c0-2.4 1.6-4.5 3.8-5.2.5-1 1.5-1.8 2.7-1.8.8 0 1.5.3 2 .7.4-.3.9-.5 1.5-.7z" transform="scale(0.85) translate(2, 3)"/>
      </svg>
    ),
    zoho: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
        <rect x="3" y="6" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
        <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor">Z</text>
      </svg>
    ),
    airtable: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 7l9-4 9 4v2l-9 4-9-4V7z" opacity="0.8"/>
        <path d="M3 11l9 4 9-4v2l-9 4-9-4v-2z" opacity="0.6"/>
        <path d="M3 15l9 4 9-4v2l-9 4-9-4v-2z" opacity="0.4"/>
      </svg>
    ),
    resend: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M3 7l9 5 9-5" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    mailjet: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
        <path d="M2 6l10 6L22 6v12H2V6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        <path d="M15 12l7-6" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    brevo: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 12l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    sendgrid: (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
        <rect x="2" y="2" width="8" height="8" rx="1" opacity="0.9"/>
        <rect x="14" y="2" width="8" height="8" rx="1" opacity="0.5"/>
        <rect x="2" y="14" width="8" height="8" rx="1" opacity="0.5"/>
        <rect x="14" y="14" width="8" height="8" rx="1" opacity="0.3"/>
      </svg>
    ),
  }
  return icons[provider] || <Database size={s} />
}

function getIcon(connector: Connector): JSX.Element {
  const iconName = connector.meta.icon
  if (LUCIDE_ICONS[iconName]) {
    const Icon = LUCIDE_ICONS[iconName]
    return <Icon size={18} />
  }
  return <BrandIcon provider={iconName} size={18} />
}

const PROVIDER_COLORS: Record<string, string> = {
  // Google
  google_search_console: '#4285F4',
  google_business_profile: '#34A853',
  google_ads: '#FBBC05',
  google_analytics: '#EA4335',
  // AI
  openai: '#10A37F',
  anthropic: '#D4A574',
  gemini: '#8B5CF6',
  // CRM
  hubspot: '#FF7A59',
  pipedrive: '#017737',
  salesforce: '#00A1E0',
  zoho_crm: '#D32F2F',
  airtable: '#FCBF49',
  // Email
  resend: '#000000',
  mailjet: '#F4A900',
  brevo: '#0092FF',
  sendgrid: '#1A82E2',
}

type Connector = {
  id: string
  provider: string
  category: string
  status: string
  account_label: string | null
  has_credentials: boolean
  last_used_at: string | null
  last_error: string | null
  created_at: string | null
  meta: { category: string; label: string; icon: string }
}

// ── Status badge ────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    connected: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400', label: 'Connecté' },
    error: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400', label: 'Erreur' },
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', label: 'En attente' },
    disconnected: { bg: 'bg-paper-warm', text: 'text-ink-faint', dot: 'bg-ink-faint/30', label: 'Non connecté' },
  }
  const c = config[status] || config.disconnected
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold', c.bg, c.text)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', c.dot)} />
      {c.label}
    </span>
  )
}

// ── API Key Modal ───────────────────────────────────────

function ApiKeyModal({
  provider,
  label,
  color,
  placeholder,
  onClose,
  onSave,
}: {
  provider: string
  label: string
  color: string
  placeholder: string
  onClose: () => void
  onSave: () => void
}) {
  const [apiKey, setApiKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!apiKey.trim() || apiKey.length < 3) {
      setError('Clé API trop courte.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await api.post('/connectors/api-key', { provider, api_key: apiKey.trim() })
      onSave()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la sauvegarde.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
              <BrandIcon provider={PROVIDER_META_ICON[provider] || provider} />
            </div>
            <h3 className="text-base font-semibold text-ink">{label}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm">
            <X size={16} className="text-ink-faint" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1.5">Clé API</label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={placeholder}
                className="w-full px-3 py-2.5 pr-10 text-sm font-mono border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
              />
              <button type="button" onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 bg-paper rounded-lg border border-ink-border/50">
            <Shield size={14} className="text-gold mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-ink-muted leading-relaxed">
              Votre clé est chiffrée (AES-256) avant stockage. Elle n'est jamais partagée entre workspaces.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
            <button onClick={handleSave} disabled={loading || !apiKey.trim()}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
              {loading && <Loader2 size={14} className="animate-spin" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper to map provider to icon name for the modal
const PROVIDER_META_ICON: Record<string, string> = {
  hubspot: 'hubspot', pipedrive: 'pipedrive', salesforce: 'salesforce',
  zoho_crm: 'zoho', airtable: 'airtable',
  resend: 'resend', mailjet: 'mailjet', brevo: 'brevo', sendgrid: 'sendgrid',
  openai: 'brain', anthropic: 'sparkles', gemini: 'stars',
}

// ── Google Connect Modal ────────────────────────────────

function GoogleConnectModal({ connectors, onClose }: { connectors: Connector[]; onClose: () => void }) {
  const googleProviders = connectors.filter((c) => c.category === 'google')
  const [selected, setSelected] = useState<string[]>(
    googleProviders.filter((c) => c.status !== 'connected').map((c) => c.provider)
  )
  const [loading, setLoading] = useState(false)

  const toggle = (p: string) => {
    setSelected((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p])
  }

  const handleConnect = async () => {
    if (selected.length === 0) return
    setLoading(true)
    try {
      const { data } = await api.post('/connectors/google/connect', { providers: selected })
      if (data.data?.auth_url) window.location.href = data.data.auth_url
    } catch { setLoading(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-ink">Connecter Google</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm">
            <X size={16} className="text-ink-faint" />
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-4">Sélectionnez les services. Un seul écran de consentement Google autorisera tous les accès.</p>
        <div className="space-y-2 mb-5">
          {googleProviders.map((c) => {
            const color = PROVIDER_COLORS[c.provider]
            const isSelected = selected.includes(c.provider)
            const isConnected = c.status === 'connected'
            return (
              <label key={c.provider} className={cn(
                'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all',
                isConnected ? 'border-emerald-200 bg-emerald-50/50 opacity-60'
                  : isSelected ? 'border-gold bg-gold-light/30 shadow-sm'
                  : 'border-ink-border hover:bg-paper',
              )}>
                <input type="checkbox" checked={isSelected || isConnected} disabled={isConnected}
                  onChange={() => !isConnected && toggle(c.provider)} className="accent-gold" />
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}12`, color }}>
                  {getIcon(c)}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink">{c.meta.label}</p>
                  {isConnected && <p className="text-[10px] text-emerald-600">Déjà connecté — {c.account_label}</p>}
                </div>
              </label>
            )
          })}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
          <button onClick={handleConnect} disabled={loading || selected.length === 0}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
            Connecter avec Google
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Connector Card ──────────────────────────────────────

function ConnectorCard({
  connector,
  onConnect,
  onDisconnect,
  onTest,
}: {
  connector: Connector
  onConnect: () => void
  onDisconnect: () => void
  onTest: () => void
}) {
  const color = PROVIDER_COLORS[connector.provider] || '#666'
  const isConnected = connector.status === 'connected'
  const [testing, setTesting] = useState(false)

  const handleTest = async () => {
    setTesting(true)
    await onTest()
    setTesting(false)
  }

  return (
    <div className={cn(
      'bg-white rounded-card border p-4 transition-all hover:shadow-elevated',
      isConnected ? 'border-emerald-200/60' : 'border-ink-border',
    )}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}12`, color }}>
            {getIcon(connector)}
          </div>
          <div>
            <p className="text-sm font-semibold text-ink leading-tight">{connector.meta.label}</p>
            <StatusBadge status={connector.status} />
          </div>
        </div>
      </div>

      {connector.account_label && (
        <p className="text-[11px] text-ink-muted mb-2 font-mono bg-paper rounded px-2 py-1 truncate">
          {connector.account_label}
        </p>
      )}

      {connector.last_error && (
        <div className="flex items-start gap-1.5 mb-2 p-2 bg-red-50 rounded-lg">
          <AlertCircle size={11} className="text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-[10px] text-red-600 line-clamp-2">{connector.last_error}</p>
        </div>
      )}

      <div className="flex items-center gap-2 mt-auto pt-2 border-t border-ink-border/30">
        {isConnected ? (
          <>
            <button onClick={handleTest} disabled={testing}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-ink bg-paper-warm hover:bg-paper rounded-lg transition-colors">
              {testing ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} Tester
            </button>
            <button onClick={onDisconnect}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-auto">
              <Trash2 size={11} /> Retirer
            </button>
          </>
        ) : (
          <button onClick={onConnect}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg transition-colors w-full justify-center"
            style={{ background: color }}>
            Connecter <ChevronRight size={11} />
          </button>
        )}
      </div>
    </div>
  )
}

// ── Section Header ──────────────────────────────────────

function SectionHeader({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        <p className="text-xs text-ink-muted mt-0.5">{description}</p>
      </div>
      {action}
    </div>
  )
}

// ══════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════

export function ConnectorsPage() {
  const user = useAuthStore((s) => s.user)
  const [searchParams] = useSearchParams()
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [sharedKeys, setSharedKeys] = useState<Record<string, boolean>>({})
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [showApiKeyModal, setShowApiKeyModal] = useState<{ provider: string; label: string; color: string; placeholder: string } | null>(null)
  const [showGoogleModal, setShowGoogleModal] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')

  const fetchConnectors = async () => {
    try {
      const { data } = await api.get('/connectors')
      setConnectors(data.data.connectors)
      setSharedKeys(data.data.shared_keys || {})
      setPlaceholders(data.data.placeholders || {})
    } catch { /* */ } finally { setLoading(false) }
  }

  useEffect(() => { fetchConnectors() }, [])

  useEffect(() => {
    if (searchParams.get('google') === 'connected') {
      setSuccessMsg('Compte Google connecté avec succès !')
      setTimeout(() => setSuccessMsg(''), 4000)
      window.history.replaceState({}, '', '/settings/connectors')
    }
  }, [searchParams])

  const handleDisconnect = async (id: string) => {
    if (!confirm('Déconnecter ce service ?')) return
    try { await api.delete(`/connectors/${id}`); fetchConnectors() } catch { /* */ }
  }

  const handleTest = async (id: string) => {
    try { await api.post(`/connectors/${id}/test`); fetchConnectors() } catch { /* */ }
  }

  const openApiKeyModal = (c: Connector) => {
    setShowApiKeyModal({
      provider: c.provider,
      label: c.meta.label,
      color: PROVIDER_COLORS[c.provider] || '#666',
      placeholder: placeholders[c.provider] || 'Votre clé API...',
    })
  }

  const googleConnectors = connectors.filter((c) => c.category === 'google')
  const aiConnectors = connectors.filter((c) => c.category === 'ai')
  const crmConnectors = connectors.filter((c) => c.category === 'crm')
  const emailConnectors = connectors.filter((c) => c.category === 'email')

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-ink-faint" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Connecteurs</h1>
        <p className="text-ink-muted mt-1">Connectez vos comptes et services externes pour alimenter les modules TremorApp.</p>
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl animate-fade-in">
          <Check size={16} className="text-emerald-600" />
          <p className="text-sm text-emerald-700 font-medium">{successMsg}</p>
        </div>
      )}

      {/* ── Google Services ────────────────────── */}
      <section>
        <SectionHeader
          title="Services Google"
          description="Connectez votre compte Google pour activer la synchronisation des données."
          action={
            <button onClick={() => setShowGoogleModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-onyx hover:bg-onyx-light rounded-card transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Connecter Google
            </button>
          }
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {googleConnectors.map((c) => (
            <ConnectorCard key={c.provider} connector={c}
              onConnect={() => setShowGoogleModal(true)}
              onDisconnect={() => handleDisconnect(c.id)}
              onTest={() => handleTest(c.id)} />
          ))}
        </div>
      </section>

      {/* ── CRM ──────────────────────────────── */}
      <section>
        <SectionHeader
          title="CRM"
          description="Connectez votre CRM pour synchroniser les contacts et déclencher les campagnes ARE."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {crmConnectors.map((c) => (
            <ConnectorCard key={c.provider} connector={c}
              onConnect={() => openApiKeyModal(c)}
              onDisconnect={() => handleDisconnect(c.id)}
              onTest={() => handleTest(c.id)} />
          ))}
        </div>
      </section>

      {/* ── Email ────────────────────────────── */}
      <section>
        <SectionHeader
          title="Email"
          description="Configurez votre service d'envoi d'emails pour les campagnes et notifications."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {emailConnectors.map((c) => {
            const hasShared = sharedKeys[c.provider]
            return (
              <div key={c.provider}>
                <ConnectorCard connector={c}
                  onConnect={() => openApiKeyModal(c)}
                  onDisconnect={() => handleDisconnect(c.id)}
                  onTest={() => handleTest(c.id)} />
                {!c.has_credentials && hasShared && (
                  <div className="flex items-center gap-1.5 mt-2 px-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold/60" />
                    <span className="text-[10px] text-ink-faint">Clé mutualisée active</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* ── AI Providers ──────────────────────── */}
      <section>
        <SectionHeader
          title="Fournisseurs IA"
          description="Ajoutez votre propre clé API pour contrôler vos coûts. Sans clé personnelle, TremorApp utilise sa clé mutualisée."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {aiConnectors.map((c) => {
            const hasShared = sharedKeys[c.provider]
            return (
              <div key={c.provider}>
                <ConnectorCard connector={c}
                  onConnect={() => openApiKeyModal(c)}
                  onDisconnect={() => handleDisconnect(c.id)}
                  onTest={() => handleTest(c.id)} />
                {!c.has_credentials && hasShared && (
                  <div className="flex items-center gap-1.5 mt-2 px-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold/60" />
                    <span className="text-[10px] text-ink-faint">Clé mutualisée disponible</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* ── Info box ──────────────────────────── */}
      <div className="bg-onyx rounded-card p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
          <Shield size={20} className="text-gold" />
        </div>
        <div>
          <p className="text-sm font-semibold text-paper mb-1">Sécurité des données</p>
          <p className="text-xs text-paper/60 leading-relaxed">
            Toutes les clés API et tokens OAuth sont chiffrés en AES-256 (Fernet) avant stockage.
            Les tokens Google sont automatiquement rafraîchis. Vos clés personnelles sont prioritaires
            sur les clés mutualisées et ne sont jamais partagées entre workspaces.
          </p>
        </div>
      </div>

      {/* ── Modals ────────────────────────────── */}
      {showApiKeyModal && (
        <ApiKeyModal
          provider={showApiKeyModal.provider}
          label={showApiKeyModal.label}
          color={showApiKeyModal.color}
          placeholder={showApiKeyModal.placeholder}
          onClose={() => setShowApiKeyModal(null)}
          onSave={() => { setShowApiKeyModal(null); fetchConnectors() }}
        />
      )}
      {showGoogleModal && (
        <GoogleConnectModal connectors={connectors} onClose={() => setShowGoogleModal(false)} />
      )}
    </div>
  )
}
